import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  Stack,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
} from '@mui/material';
import {
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  Description as DescriptionIcon,
  CardMembership as CardIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  CloudUpload as CloudUploadIcon,
  Print as PrintIcon,
  VerifiedUser as VerifiedUserIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { colors } from '../colors';

const LandingPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [loginMenuAnchor, setLoginMenuAnchor] = useState<null | HTMLElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const benefitsRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsVisible(true);
    
    // Intersection Observer for scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      }
    }, observerOptions);

    const refs = [heroRef, featuresRef, benefitsRef, ctaRef];
    for (const ref of refs) {
      if (ref.current) {
        observer.observe(ref.current);
      }
    }

    return () => {
      for (const ref of refs) {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      }
    };
  }, []);

  const features = [
    {
      icon: <AssignmentIcon sx={{ fontSize: 50, color: colors.primary }} />,
      title: t('landing_feature_admission_form'),
      description: t('landing_feature_admission_form_desc'),
    },
    {
      icon: <DescriptionIcon sx={{ fontSize: 50, color: colors.primary }} />,
      title: t('landing_feature_marksheet'),
      description: t('landing_feature_marksheet_desc'),
    },
    {
      icon: <AssessmentIcon sx={{ fontSize: 50, color: colors.primary }} />,
      title: t('landing_feature_gosvara'),
      description: t('landing_feature_gosvara_desc'),
    },
    {
      icon: <CardIcon sx={{ fontSize: 50, color: colors.primary }} />,
      title: t('landing_feature_admit_cards'),
      description: t('landing_feature_admit_cards_desc'),
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 50, color: colors.primary }} />,
      title: t('landing_feature_student_management'),
      description: t('landing_feature_student_management_desc'),
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 50, color: colors.primary }} />,
      title: t('landing_feature_teacher_management'),
      description: t('landing_feature_teacher_management_desc'),
    },
    {
      icon: <VerifiedUserIcon sx={{ fontSize: 50, color: colors.primary }} />,
      title: t('landing_feature_aadhar'),
      description: t('landing_feature_aadhar_desc'),
    },
    {
      icon: <PrintIcon sx={{ fontSize: 50, color: colors.primary }} />,
      title: t('landing_feature_bulk_printing'),
      description: t('landing_feature_bulk_printing_desc'),
    },
    {
      icon: <CloudUploadIcon sx={{ fontSize: 50, color: colors.primary }} />,
      title: t('landing_feature_document_management'),
      description: t('landing_feature_document_management_desc'),
    },
  ];

  const benefits = [
    {
      icon: <SpeedIcon sx={{ fontSize: 40, color: colors.success }} />,
      title: t('landing_benefit_fast'),
      description: t('landing_benefit_fast_desc'),
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: colors.success }} />,
      title: t('landing_benefit_secure'),
      description: t('landing_benefit_secure_desc'),
    },
    {
      icon: <SchoolIcon sx={{ fontSize: 40, color: colors.success }} />,
      title: t('landing_benefit_comprehensive'),
      description: t('landing_benefit_comprehensive_desc'),
    },
  ];

  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        background: `linear-gradient(135deg, ${colors.primaryLight} 0%, white 50%)`,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          width: '100%',
          height: '100%',
          top: 0,
          left: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(48, 146, 227, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(58, 150, 251, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
          animation: 'backgroundMove 20s ease-in-out infinite',
          '@keyframes backgroundMove': {
            '0%, 100%': {
              transform: 'translate(0, 0)',
            },
            '50%': {
              transform: 'translate(20px, 20px)',
            },
          },
        },
      }}
    >
      {/* Header/Navigation */}
      <AppBar 
        position="static" 
        elevation={0} 
        sx={{ 
          bgcolor: 'white', 
          color: 'text.primary',
          position: 'fixed',
          zIndex: 1000,
          animation: 'slideDown 0.5s ease-out',
          '@keyframes slideDown': {
            '0%': {
              transform: 'translateY(-100%)',
              opacity: 0,
            },
            '100%': {
              transform: 'translateY(0)',
              opacity: 1,
            },
          },
        }}
      >
        <Toolbar>
          <SchoolIcon 
            sx={{ 
              fontSize: 40, 
              color: colors.primary, 
              mr: 2,
              animation: 'rotateIn 0.8s ease-out',
              '@keyframes rotateIn': {
                '0%': {
                  transform: 'rotate(-180deg) scale(0)',
                  opacity: 0,
                },
                '100%': {
                  transform: 'rotate(0deg) scale(1)',
                  opacity: 1,
                },
              },
            }} 
          />
          <Typography 
            variant="h5" 
            component="div" 
            sx={{ 
              flexGrow: 1, 
              fontWeight: 700, 
              color: colors.primary,
              animation: 'fadeInLeft 0.6s ease-out 0.2s both',
              '@keyframes fadeInLeft': {
                '0%': {
                  opacity: 0,
                  transform: 'translateX(-30px)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateX(0)',
                },
              },
            }}
          >
            {t('landing_school_management_system')}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl
              size="small"
              sx={{
                minWidth: 120,
                
                '& .MuiSelect-select': {
                  fontWeight: 500,
                  // color: colors.darkGray,
                  py: 1,
                },
              }}
            >
              <InputLabel sx={{ color: colors.gray }}>{t('language')}</InputLabel>
              <Select
                value={i18n.language}
                onChange={(e) => {
                  const newLang = e.target.value;
                  i18n.changeLanguage(newLang);
                  // Persist language selection
                  try {
                    localStorage.setItem('selectedLanguage', newLang);
                    localStorage.setItem('i18nextLng', newLang);
                  } catch (error) {
                  }
                }}
                label={t('language')}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: 2,
                      border: `1px solid ${colors.primaryLight}`,
                      mt: 1,
                      '& .MuiMenuItem-root': {
                        borderRadius: 1,
                        mx: 1,
                        my: 0.5,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: 'rgba(48, 146, 227, 0.08)',
                          transform: 'translateX(4px)',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(48, 146, 227, 0.15)',
                          color: colors.primary,
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: 'rgba(48, 146, 227, 0.2)',
                          },
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="hi">हिन्दी</MenuItem>
                <MenuItem value="en">English</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={(e) => {
                setLoginMenuAnchor(e.currentTarget);
              }}
              sx={{
                bgcolor: colors.primary,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(1, 104, 234, 0.3)',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                  transition: 'left 0.5s ease',
                },
                '&:hover': {
                  bgcolor: colors.secondary,
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(1, 104, 234, 0.4)',
                  '&::before': {
                    left: '100%',
                  },
                },
                '&:active': {
                  transform: 'translateY(0) scale(0.98)',
                },
              }}
            >
              {t('landing_login')}
            </Button>
            <Menu
              anchorEl={loginMenuAnchor}
              open={Boolean(loginMenuAnchor)}
              onClose={() => setLoginMenuAnchor(null)}
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  border: `1px solid ${colors.primaryLight}`,
                  mt: 1.5,
                  minWidth: 240,
                  boxShadow: '0 8px 24px rgba(48, 146, 227, 0.2)',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: `linear-gradient(90deg, ${colors.primary}, ${colors.secondary})`,
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem
                onClick={() => {
                  setLoginMenuAnchor(null);
                  navigate('/login?type=student');
                }}
                sx={{
                  py: 2,
                  px: 2.5,
                  gap: 2,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: 'rgba(48, 146, 227, 0.12)',
                    transform: 'translateX(6px)',
                    '& .menu-icon': {
                      transform: 'scale(1.15) rotate(5deg)',
                      color: colors.primary,
                    },
                    '& .menu-text': {
                      fontWeight: 600,
                      color: colors.primary,
                    },
                  },
                }}
              >
                <PersonIcon 
                  className="menu-icon"
                  sx={{ 
                    color: colors.primary, 
                    fontSize: 24,
                    transition: 'all 0.3s ease',
                  }} 
                />
                <Typography 
                  className="menu-text"
                  variant="body1" 
                  sx={{ 
                    fontWeight: 500,
                    transition: 'all 0.3s ease',
                    color: colors.darkGray,
                  }}
                >
                  {t('landing_login_as_student') || 'Student'}
                </Typography>
              </MenuItem>
              <Box
                sx={{
                  height: '1px',
                  background: `linear-gradient(90deg, transparent, ${colors.primaryLight}, transparent)`,
                  mx: 1,
                }}
              />
              <MenuItem
                onClick={() => {
                  setLoginMenuAnchor(null);
                  navigate('/login?type=staff');
                }}
                sx={{
                  py: 2,
                  px: 2.5,
                  gap: 2,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: 'rgba(48, 146, 227, 0.12)',
                    transform: 'translateX(6px)',
                    '& .menu-icon': {
                      transform: 'scale(1.15) rotate(-5deg)',
                      color: colors.secondary,
                    },
                    '& .menu-text': {
                      fontWeight: 600,
                      color: colors.secondary,
                    },
                  },
                }}
              >
                <AdminIcon 
                  className="menu-icon"
                  sx={{ 
                    color: colors.secondary, 
                    fontSize: 24,
                    transition: 'all 0.3s ease',
                  }} 
                />
                <Typography 
                  className="menu-text"
                  variant="body1" 
                  sx={{ 
                    fontWeight: 500,
                    transition: 'all 0.3s ease',
                    color: colors.darkGray,
                  }}
                >
                  {t('landing_login_as_staff') || 'Staff/Admin'}
                </Typography>
              </MenuItem>
            </Menu>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        ref={heroRef}
        sx={{
          pt: { xs: 8, md: 12 },
          pb: { xs: 6, md: 10 },
          position: 'relative',
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.8s ease-in',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant={isMobile ? 'h3' : 'h2'}
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  color: colors.darkGray,
                  lineHeight: 1.2,
                  animation: 'fadeInUp 0.8s ease-out 0.2s both',
                  '@keyframes fadeInUp': {
                    '0%': {
                      opacity: 0,
                      transform: 'translateY(30px)',
                    },
                    '100%': {
                      opacity: 1,
                      transform: 'translateY(0)',
                    },
                  },
                }}
              >
                {t('landing_complete_school_management')}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mb: 4,
                  color: colors.gray,
                  lineHeight: 1.8,
                  fontWeight: 400,
                  animation: 'fadeInUp 0.8s ease-out 0.4s both',
                }}
              >
                {t('landing_hero_description')}
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/contact-us')}
                sx={{
                  position: 'relative',
                  overflow: 'visible',
                  background: 'linear-gradient(135deg, #ff416c 0%, #ff4b2b 50%, #ff6b5a 100%)',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 800,
                  // fontSize: { xs: '2.45rem', md: '2.5rem' },
                  px: { xs: 5, md: 6 },
                  py: { xs: 2, md: 1.6 },
                  borderRadius: 999,
                  minWidth: { xs: '100%', sm: 420, md: 480 },
                  width: { xs: '100%', sm: 'auto' },
                  boxShadow: '0 12px 35px rgba(255, 65, 108, 0.5), 0 0 0 0 rgba(255, 65, 108, 0.7)',
                  transition: 'all 0.4s cubic-bezier(0.19, 1, 0.22, 1)',
                  animation: 'fadeInUp 0.8s ease-out 0.6s both, pulseGlow 2s ease-in-out infinite 1s',
                  letterSpacing: 0.8,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 2,
                  '@keyframes pulseGlow': {
                    '0%, 100%': {
                      boxShadow: '0 12px 35px rgba(255, 65, 108, 0.5), 0 0 0 0 rgba(255, 65, 108, 0.7)',
                    },
                    '50%': {
                      boxShadow: '0 12px 35px rgba(255, 65, 108, 0.6), 0 0 0 8px rgba(255, 65, 108, 0)',
                    },
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: -2,
                    borderRadius: 999,
                    background: 'linear-gradient(135deg, #ff416c, #ff4b2b, #ff6b5a)',
                    opacity: 0.3,
                    filter: 'blur(8px)',
                    zIndex: -1,
                    transition: 'opacity 0.4s ease',
                  },
                  '&:hover': {
                    transform: 'translateY(-6px) scale(1.05)',
                    boxShadow: '0 20px 50px rgba(255, 65, 108, 0.7), 0 0 0 0 rgba(255, 65, 108, 0.9)',
                    background: 'linear-gradient(135deg, #ff4d6d 0%, #ff3333 50%, #ff7a6b 100%)',
                    '&::before': {
                      opacity: 0.5,
                    },
                  },
                  '&:active': {
                    transform: 'translateY(-2px) scale(1.02)',
                    boxShadow: '0 8px 25px rgba(255, 65, 108, 0.6)',
                  },
                  '&:hover .cta-arrow': {
                    transform: 'translateX(8px) scale(1.1)',
                  },
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: `'Lato', 'Roboto', 'Segoe UI', sans-serif`,
                      fontWeight: 800,
                      letterSpacing: 1,
                      fontSize: { xs: '2.25rem', md: 28 },
                      textTransform: 'uppercase',
                      textShadow: '0 2px 4px rgba(0,0,0,0.25)',
                    }}
                  >
                    Contact Us
                  </Typography>
                  <ArrowForwardIcon
                    className="cta-arrow"
                    sx={{
                      fontSize: { xs: 26, md: 30 },
                      transition: 'transform 0.3s ease',
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                    }}
                  />
                </Box>
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  minHeight: { xs: 420, md: 520 },
                  gap: { xs: 1.5, md: 2 },
                  animation: 'fadeInRight 1s ease-out 0.6s both',
                  '@keyframes fadeInRight': {
                    '0%': {
                      opacity: 0,
                      transform: 'translateX(50px)',
                    },
                    '100%': {
                      opacity: 1,
                      transform: 'translateX(0)',
                    },
                  },
                }}
              >
                <SchoolIcon
                  sx={{
                    fontSize: { xs: 160, md: 170 },
                    color: colors.primary,
                    opacity: 0.25,
                    animation: 'float 6s ease-in-out infinite',
                    '@keyframes float': {
                      '0%, 100%': {
                        transform: 'translateY(0px) rotate(0deg)',
                      },
                      '50%': {
                        transform: 'translateY(-20px) rotate(5deg)',
                      },
                    },
                  }}
                />

                <Typography
                  variant={isMobile ? 'h4' : 'h3'}
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    color: colors.darkGray,
                    lineHeight: 1.2,
                    maxWidth: 520,
                    mx: 'auto',
                    animation: 'fadeInUp 0.8s ease-out 0.2s both',
                    '@keyframes fadeInUp': {
                      '0%': {
                        opacity: 0,
                        transform: 'translateY(30px)',
                      },
                      '100%': {
                        opacity: 1,
                        transform: 'translateY(0)',
                      },
                    },
                  }}
                >
                  Click here to begin your application form.
                </Typography>

                <Button
                  variant="contained"
                  onClick={() => navigate('/register')}
                  sx={{
                    position: 'relative',
                    overflow: 'visible',
                    background: 'linear-gradient(135deg, #ff416c 0%, #ff4b2b 50%, #ff6b5a 100%)',
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 800,
                    px: { xs: 5, md: 6 },
                    py: { xs: 2, md: 2.5 },
                    borderRadius: 999,
                    minWidth: { xs: '100%', sm: 420, md: 480 },
                    width: { xs: '100%', sm: 'auto' },
                    boxShadow: '0 12px 35px rgba(255, 65, 108, 0.5), 0 0 0 0 rgba(255, 65, 108, 0.7)',
                    transition: 'all 0.4s cubic-bezier(0.19, 1, 0.22, 1)',
                    animation: 'fadeInUp 0.8s ease-out 0.6s both, pulseGlow 2s ease-in-out infinite 1s',
                    letterSpacing: 0.8,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 2,
                    '@keyframes pulseGlow': {
                      '0%, 100%': {
                        boxShadow: '0 12px 35px rgba(255, 65, 108, 0.5), 0 0 0 0 rgba(255, 65, 108, 0.7)',
                      },
                      '50%': {
                        boxShadow: '0 12px 35px rgba(255, 65, 108, 0.6), 0 0 0 8px rgba(255, 65, 108, 0)',
                      },
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      inset: -2,
                      borderRadius: 999,
                      background: 'linear-gradient(135deg, #ff416c, #ff4b2b, #ff6b5a)',
                      opacity: 0.3,
                      filter: 'blur(8px)',
                      zIndex: -1,
                      transition: 'opacity 0.4s ease',
                    },
                    '&:hover': {
                      transform: 'translateY(-6px) scale(1.05)',
                      boxShadow: '0 20px 50px rgba(255, 65, 108, 0.7), 0 0 0 0 rgba(255, 65, 108, 0.9)',
                      background: 'linear-gradient(135deg, #ff4d6d 0%, #ff3333 50%, #ff7a6b 100%)',
                      '&::before': {
                        opacity: 0.5,
                      },
                    },
                    '&:active': {
                      transform: 'translateY(-2px) scale(1.02)',
                      boxShadow: '0 8px 25px rgba(255, 65, 108, 0.6)',
                    },
                    '&:hover .cta-arrow': {
                      transform: 'translateX(8px) scale(1.1)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      zIndex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontFamily: `'Lato', 'Roboto', 'Segoe UI', sans-serif`,
                        fontWeight: 800,
                        letterSpacing: 1,
                        fontSize: { xs: '1.15rem', md: '1.25rem' },
                        textTransform: 'uppercase',
                        textShadow: '0 2px 4px rgba(0,0,0,0.25)',
                      }}
                    >
                      School Selection Portal
                    </Typography>
                    <ArrowForwardIcon
                      className="cta-arrow"
                      sx={{
                        fontSize: { xs: 24, md: 28 },
                        transition: 'transform 0.3s ease',
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                      }}
                    />
                  </Box>
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box 
        ref={featuresRef}
        sx={{ 
          py: { xs: 6, md: 10 }, 
          bgcolor: 'white', 
          overflow: 'hidden',
          position: 'relative',
          '@keyframes fadeInUp': {
            '0%': {
              opacity: 0,
              transform: 'translateY(30px)',
            },
            '100%': {
              opacity: 1,
              transform: 'translateY(0)',
            },
          },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: colors.darkGray,
                animation: 'fadeInUp 0.8s ease-out 0.2s both',
              }}
            >
              {t('landing_useful_features')}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: colors.gray,
                maxWidth: 700,
                mx: 'auto',
                animation: 'fadeInUp 0.8s ease-out 0.4s both',
              }}
            >
              {t('landing_features_subtitle')}
            </Typography>
          </Box>
        </Container>

        {/* Marquee Container - Full Width */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            overflow: 'hidden',
            mt: 4,
            pt:2,
          }}
        >
            <Box
              sx={{
                display: 'flex',
                gap: 3,
                width: 'fit-content',
                animation: 'marquee 30s linear infinite',
                '@keyframes marquee': {
                  '0%': {
                    transform: 'translateX(0)',
                  },
                  '100%': {
                    transform: 'translateX(-33.333%)',
                  },
                },
                '&:hover': {
                  animationPlayState: 'paused',
                },
              }}
            >
              {/* Duplicate features multiple times for seamless infinite loop */}
              {[...features, ...features, ...features].map((feature, index) => (
                <Box
                  key={`marquee-${feature.title}-${index}`}
                  sx={{
                    flexShrink: 0,
                    width: { xs: '280px', sm: '320px', md: '350px' },
                  }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: `1px solid ${colors.lightLightGray}`,
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: `linear-gradient(90deg, transparent, ${colors.primaryLight}, transparent)`,
                        transition: 'left 0.5s ease',
                      },
                      '&:hover': {
                        transform: 'translateY(-12px) scale(1.02)',
                        boxShadow: '0 12px 40px rgba(48, 146, 227, 0.25)',
                        borderColor: colors.primary,
                        '&::before': {
                          left: '100%',
                        },
                        '& .feature-icon': {
                          transform: 'scale(1.1) rotate(5deg)',
                          color: colors.secondary,
                        },
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                      <Box 
                        className="feature-icon"
                        sx={{ 
                          mb: 2,
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          display: 'inline-block',
                        }}
                      >
                        {feature.icon}
                      </Box>
                      <Typography
                        variant="h6"
                        component="h3"
                        sx={{
                          fontWeight: 600,
                          mb: 1.5,
                          color: colors.darkGray,
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.gray,
                          lineHeight: 1.7,
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          </Box>
      </Box>

      {/* Benefits Section */}
      <Box 
        ref={benefitsRef}
        sx={{ 
          py: { xs: 6, md: 10 }, 
          bgcolor: colors.primaryLight,
          position: 'relative',
          '@keyframes fadeInUp': {
            '0%': {
              opacity: 0,
              transform: 'translateY(30px)',
            },
            '100%': {
              opacity: 1,
              transform: 'translateY(0)',
            },
          },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: colors.darkGray,
                animation: 'fadeInUp 0.8s ease-out 0.2s both',
              }}
            >
              {t('landing_why_choose_us')}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: colors.gray,
                maxWidth: 700,
                mx: 'auto',
                animation: 'fadeInUp 0.8s ease-out 0.4s both',
              }}
            >
              {t('landing_benefits_subtitle')}
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} md={4} key={`benefit-${benefit.title}-${index}`}>
                <Paper
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    height: '100%',
                    bgcolor: 'white',
                    borderRadius: 3,
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    animation: `fadeInUp 0.6s ease-out ${0.6 + index * 0.2}s both`,
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      background: `linear-gradient(90deg, ${colors.primary}, ${colors.secondary})`,
                      transform: 'scaleX(0)',
                      transformOrigin: 'left',
                      transition: 'transform 0.4s ease',
                    },
                    '&:hover': {
                      transform: 'translateY(-10px) scale(1.03)',
                      boxShadow: '0 12px 40px rgba(48, 146, 227, 0.2)',
                      '&::before': {
                        transform: 'scaleX(1)',
                      },
                      '& .benefit-icon': {
                        transform: 'scale(1.2) rotate(360deg)',
                        color: colors.primary,
                      },
                    },
                  }}
                >
                  <Box 
                    className="benefit-icon"
                    sx={{ 
                      mb: 2,
                      transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                      display: 'inline-block',
                    }}
                  >
                    {benefit.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{
                      fontWeight: 600,
                      mb: 1.5,
                      color: colors.darkGray,
                    }}
                  >
                    {benefit.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.gray,
                      lineHeight: 1.7,
                    }}
                  >
                    {benefit.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        ref={ctaRef}
        sx={{
          py: { xs: 6, md: 10 },
          bgcolor: colors.slightBlue,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
            animation: 'pulseGlow 3s ease-in-out infinite',
            '@keyframes pulseGlow': {
              '0%, 100%': {
                opacity: 0.5,
                transform: 'scale(1)',
              },
              '50%': {
                opacity: 0.8,
                transform: 'scale(1.1)',
              },
            },
          },
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: 'white',
                animation: 'fadeInUp 0.8s ease-out 0.2s both',
                '@keyframes fadeInUp': {
                  '0%': {
                    opacity: 0,
                    transform: 'translateY(30px)',
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
              }}
            >
              {t('landing_cta_title')}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 4,
                color: 'rgba(255, 255, 255, 0.9)',
                maxWidth: 600,
                mx: 'auto',
                animation: 'fadeInUp 0.8s ease-out 0.4s both',
              }}
            >
              {t('landing_cta_description')}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Contact Section - Fixed in Left Bottom Corner */}
      {/* <Box
        sx={{
          position: 'fixed',
          bottom: { xs: 16, md: 24 },
          left: { xs: 16, md: 24 },
          zIndex: 1000,
          animation: 'slideInLeft 0.8s ease-out 1s both',
          '@keyframes slideInLeft': {
            '0%': {
              opacity: 0,
              transform: 'translateX(-100px)',
            },
            '100%': {
              opacity: 1,
              transform: 'translateX(0)',
            },
          },
        }}
      >
        <Paper
          elevation={4}
          sx={{
            p: { xs: 1.5, md: 2 },
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            maxWidth: { xs: 200, md: 240 },
            minWidth: { xs: 180, md: 220 },
            border: `1px solid ${colors.primaryLight}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-5px) scale(1.02)',
              boxShadow: '0 8px 24px rgba(48, 146, 227, 0.3)',
              borderColor: colors.primary,
            },
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: colors.darkGray,
              mb: 1,
              display: 'block',
              fontSize: { xs: '0.8rem', md: '0.875rem' },
            }}
          >
            {t('landing_contact_title')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: colors.darkGray,
              mb: 1.5,
              fontSize: { xs: '0.875rem', md: '1rem' },
            }}
          >
            {t('landing_contact_name')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <PhoneIcon sx={{ color: colors.primary, fontSize: { xs: 18, md: 20 } }} />
            <Typography
              variant="body2"
              component="a"
              href="tel:+919755538966"
              sx={{
                color: colors.primary,
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.875rem', md: '1rem' },
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              97555 38966
            </Typography>
          </Box>
        </Paper>
      </Box> */}

      {/* Footer */}
      <Box 
        sx={{ 
          bgcolor: colors.darkGray, 
          color: 'white', 
          py: 4,
          animation: 'fadeIn 1s ease-out',
          '@keyframes fadeIn': {
            '0%': {
              opacity: 0,
            },
            '100%': {
              opacity: 1,
            },
          },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2,
                  '&:hover .footer-icon': {
                    transform: 'rotate(360deg) scale(1.1)',
                  },
                }}
              >
                <SchoolIcon 
                  className="footer-icon"
                  sx={{ 
                    fontSize: 30, 
                    mr: 1,
                    transition: 'transform 0.6s ease',
                  }} 
                />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {t('landing_footer_title')}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {t('landing_footer_description')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: { xs: 'left', md: 'right' } }}>
                {t('landing_copyright', { year: new Date().getFullYear() })}
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

    </Box>
  );
};

export default LandingPage;

